import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Select } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import {
  projectConnectionsSummary,
  ProjectConnectionsSummaryTable
} from 'portal/components/ProjectConnectionsSummary'
import { ColourButton, icons, Spacer } from 'portal/lib/primitives'
import { Flag } from 'portal/lib/primitives/icons/Flag'
import { AlertSettings, ProjectSettings } from './AlertSettings'
import { Files } from './Files'
import { Opacity } from '@mui/icons-material'

export interface accountProjects {
  name: string
  projectId: string
  country?: string
}

export const ProjectSelector = ({
  dataView,
  selectedProjectId,
  setSelectedProjectId
}) => {
  const [useFlags, setUseFlags] = useState(false)

  const accountProjects = useSelector<
    any,
    {
      error: any
      loading: boolean
      projects: accountProjects[]
    }
  >(state => state.accountProjects)

  const accountSummary = useSelector<
    any,
    {
      error: any
      loading: boolean
      accountConnectionsSummary: projectConnectionsSummary[]
    }
  >(state => state.accountConnectionsSummary)

  useEffect(() => {
    if (
      !selectedProjectId &&
      accountProjects.projects &&
      accountProjects.projects.length > 0
    ) {
      setSelectedProjectId(accountProjects.projects[0].projectId)
    }
  }, [accountProjects.projects, selectedProjectId, setSelectedProjectId])

  const accountProjectsForSelect = accountProjects.projects?.map(m => ({
    value: m.projectId,
    label: m.name,
    country: m.country
  }))

  useEffect(() => {
    // Loop through projects to see if more than 1 country exists, if so, enable useFlags
    const countrySet = new Set()
    accountProjectsForSelect?.forEach(project => {
      if (project.country) {
        countrySet.add(project.country)
      }
    })
    if (countrySet.size > 1) {
      setUseFlags(true)
    } else {
      setUseFlags(false)
    }
  }, [accountProjectsForSelect])

  return (
    <Container>
      <ProjectsList>
        <h4>Projects</h4>
        {(accountProjects.loading || !accountProjects.projects) && (
          <div>Loading projects...</div>
        )}
        {!accountProjects.loading && accountProjects.error && (
          <div>Error loading projects!</div>
        )}
        {!accountProjects.loading &&
          !accountProjects.error &&
          accountProjects.projects && (
            <>
              <Select
                allowClear
                showSearch
                suffixIcon={<icons.MagnifyingGlass colour={'currentColor'} />}
                optionFilterProp="label"
                style={{ width: '100%', marginBottom: '1rem' }}
                options={accountProjectsForSelect}
                placeholder="Search projects..."
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? '')
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? '').toLowerCase())
                }
                onSelect={value => {
                  setSelectedProjectId(value as string)
                }}
              />
              {accountProjects.projects.map(m => (
                <ProjectButton
                  onClick={() => {
                    setSelectedProjectId(m.projectId)
                  }}
                  key={m.projectId}
                  className={selectedProjectId === m.projectId ? 'active' : ''}
                >
                  {useFlags && m.country ? (
                    <Flag
                      country={m.country}
                      style={{
                        marginTop: '6px',
                        marginRight: '0.25rem'
                      }}
                    />
                  ) : (
                    <icons.Table
                      colour="currentColor"
                      style={{ marginTop: '0.25rem' }}
                    />
                  )}
                  <Spacer.Fine />
                  <ProjectName>{m.name}</ProjectName>
                </ProjectButton>
              ))}
            </>
          )}
      </ProjectsList>
      {!accountSummary.loading &&
        !accountSummary.error &&
        accountSummary.accountConnectionsSummary &&
        selectedProjectId && (
          <>
            {dataView === 'projectData' && (
              <ProjectConnectionsSummaryTable
                summary={
                  accountSummary.accountConnectionsSummary.find(
                    sum => sum.projectId === selectedProjectId
                  ) as projectConnectionsSummary
                }
              />
            )}
            {dataView === 'alertSettings' && (
              <AlertSettings
                project={
                  accountProjects.projects.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectSettings
                }
              />
            )}
            {dataView === 'files' && (
              <Files
                project={
                  accountProjects.projects.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectSettings
                }
              />
            )}
          </>
        )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: top;
  gap: 1rem;
`
const ProjectsList = styled.div`
  width: 15.9375rem;
  background: ${colours.WHITE};
  border-radius: 0.625rem;
  padding: 0.625rem 1rem 0.5rem 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-size: 0.9375rem;
    font-weight: 700;
  }
`
const ProjectButton = styled(ColourButton.BasicBorderless)`
  font-weight: 500;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-height: none;
  margin-bottom: 0.125rem;
  border-radius: 5px;

  &:hover,
  &:focus,
  &:active {
    background-color: ${colours.CONTROL_PANEL_GREY};
    border-color: ${colours.CONTROL_PANEL_GREY};
  }

  &.active {
    background-color: ${colours.PALE_TEAL};
  }
`
const ProjectName = styled.div`
  max-width: calc(100% - 1.125rem);
`
